import { useColorScheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface QSLogoProps {
  /** 'icon' = mark only, 'full' = mark + wordmark, 'wordmark' = text only */
  variant?: 'icon' | 'full' | 'wordmark';
  height?: number;
}

export default function QSLogo({ variant = 'full', height = 32 }: QSLogoProps) {
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? (systemMode ?? 'light') : (mode ?? 'light');
  const isDark = resolvedMode === 'dark';

  // Light: navy ring + copper Q-tail/dot; dark: copper ring + copper Q-tail/dot
  const ringColor   = isDark ? '#E5BA35' : '#1A2332';  // copper[400] | navy[900]
  const accentColor = isDark ? '#E5BA35' : '#B8860B';  // copper[400] | copper[600]
  // Light: "QUARTER" navy, "STACK" copper. Dark: "QUARTER" white, "STACK" copper
  const textPrimary = isDark ? '#FFFFFF' : '#1A2332';
  const textAccent  = isDark ? '#E5BA35' : '#B8860B';  // copper[400] | copper[600]

  // Icon dimensions — crosshair is drawn on a 32×32 grid
  const iconH = height;
  const iconW = height; // square mark

  const mark = (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      sx={{ width: iconW, height: iconH, flexShrink: 0, display: 'block' }}
      aria-hidden="true"
    >
      {/* Outer circle */}
      <circle cx="16" cy="16" r="13" stroke={ringColor} strokeWidth="2.5" fill="none" />
      {/* Crosshair — top */}
      <line x1="16" y1="3" x2="16" y2="9.5" stroke={ringColor} strokeWidth="2" strokeLinecap="round" />
      {/* Crosshair — bottom (stops before tail gap) */}
      <line x1="16" y1="22.5" x2="16" y2="26" stroke={ringColor} strokeWidth="2" strokeLinecap="round" />
      {/* Crosshair — left */}
      <line x1="3" y1="16" x2="9.5" y2="16" stroke={ringColor} strokeWidth="2" strokeLinecap="round" />
      {/* Crosshair — right */}
      <line x1="22.5" y1="16" x2="29" y2="16" stroke={ringColor} strokeWidth="2" strokeLinecap="round" />
      {/* Q tail — copper diagonal in bottom-right quadrant */}
      <path d="M 20 20 L 27 27" stroke={accentColor} strokeWidth="3" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="16" cy="16" r="2" fill={accentColor} />
    </Box>
  );

  if (variant === 'icon') return mark;

  const wordmark = (
    <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
      <Typography
        component="span"
        sx={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: height * 0.59,
          lineHeight: 1,
          letterSpacing: '0.04em',
          color: textPrimary,
          textTransform: 'uppercase',
        }}
      >
        Quarter
      </Typography>
      <Typography
        component="span"
        sx={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: height * 0.59,
          lineHeight: 1,
          letterSpacing: '0.04em',
          color: textAccent,
          textTransform: 'uppercase',
        }}
      >
        Stack
      </Typography>
    </Box>
  );

  if (variant === 'wordmark') return wordmark;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${height * 0.3}px` }}>
      {mark}
      {wordmark}
    </Box>
  );
}
